import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Phone, Mail, MapPin, Globe, Shield, Users, Award, Clock, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const About = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Navigation */}
      <nav className="bg-white/90 backdrop-blur-sm border-b border-blue-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">Fremont Tech</span>
              <span className="text-sm text-gray-500">프리몬테크</span>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/about" className="text-blue-600 font-semibold">ABOUT US</Link>
              <Link to="/services" className="text-gray-700 hover:text-blue-600 transition-colors">WHAT WE DO</Link>
              <Link to="/contact" className="text-gray-700 hover:text-blue-600 transition-colors">CONTACT US</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-8">
          <ArrowLeft className="h-4 w-4 mr-2" />
          홈으로 돌아가기
        </Link>
      </div>

      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-4 bg-blue-100 text-blue-800 hover:bg-blue-200">
            About Fremont Tech
          </Badge>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            우리는 <span className="text-blue-600">신뢰할 수 있는</span> 기술 파트너입니다
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            미국 실리콘밸리에서 10여년간 쌓은 경험과 노하우를 바탕으로<br />
            고객의 성공을 위한 최고의 솔루션을 제공합니다
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">회사 소개</h2>
              <div className="space-y-6 text-gray-600">
                <p className="text-lg">
                  Fremont Tech(프리몬테크)는 미국 실리콘밸리에서 10여년간 운영되어온 
                  기술 전문 기업입니다. 우리는 웹사이트 제작, 앱 개발, CCTV 상담 및 설치 
                  서비스를 통해 고객의 디지털 혁신을 지원합니다.
                </p>
                <p>
                  "한 번 고객은 영원한 친구입니다"라는 모토 아래, 우리는 단순한 서비스 
                  제공을 넘어 고객과의 장기적인 파트너십을 구축하고 있습니다.
                </p>
                <p>
                  사용자의 입장을 잘 고려하여 서비스하는 것이 우리의 핵심 가치이며, 
                  이를 통해 고객 만족도를 극대화하고 있습니다.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <Card className="text-center p-6">
                <Clock className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">10+</h3>
                <p className="text-gray-600">년의 경험</p>
              </Card>
              <Card className="text-center p-6">
                <Users className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">100+</h3>
                <p className="text-gray-600">만족한 고객</p>
              </Card>
              <Card className="text-center p-6">
                <Award className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">대기업</h3>
                <p className="text-gray-600">프로젝트 경험</p>
              </Card>
              <Card className="text-center p-6">
                <Shield className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">신뢰</h3>
                <p className="text-gray-600">기반 서비스</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Details */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">풍부한 프로젝트 경험</h2>
            <p className="text-xl text-gray-600">다양한 규모의 프로젝트를 성공적으로 완수한 경험</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="p-8">
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl text-blue-600">대기업 프로젝트</CardTitle>
                <CardDescription className="text-lg">글로벌 기업들과의 협업 경험</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span><strong>Walmart:</strong> 대형 매장 CCTV 시스템 설치 및 관리</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span><strong>FedEx:</strong> 물류센터 보안 시스템 구축</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span><strong>DHL:</strong> 배송센터 모니터링 시스템</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span><strong>Nike:</strong> 매장 보안 솔루션 제공</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="p-8">
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl text-blue-600">공공기관 프로젝트</CardTitle>
                <CardDescription className="text-lg">지역사회를 위한 공공 프로젝트</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span><strong>지역 학교:</strong> 캠퍼스 보안 시스템 구축</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span><strong>도서관:</strong> 공공시설 모니터링 시스템</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span><strong>커뮤니티 센터:</strong> 종합 보안 솔루션</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span><strong>공공시설:</strong> 네트워크 카메라 시스템</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">우리의 가치</h2>
            <p className="text-xl text-gray-600">고객 중심의 서비스 철학</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <Users className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">사용자 중심</h3>
              <p className="text-gray-600">
                사용자의 입장을 잘 고려하여 최적의 사용자 경험을 제공합니다. 
                복잡한 기술을 간단하고 직관적으로 만드는 것이 우리의 전문성입니다.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <Shield className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">신뢰와 품질</h3>
              <p className="text-gray-600">
                10년간의 경험을 바탕으로 검증된 기술과 안정적인 서비스를 제공합니다. 
                품질에 대한 타협은 없습니다.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <Award className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">지속적인 파트너십</h3>
              <p className="text-gray-600">
                "한 번 고객은 영원한 친구입니다." 단발성 프로젝트가 아닌 
                장기적인 파트너십을 통해 고객의 성장을 함께합니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            함께 성공하는 파트너가 되어보세요
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            무료 상담을 통해 귀하의 프로젝트에 대해 이야기해보세요
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="px-8 py-3">
              <Phone className="mr-2 h-5 w-5" />
              무료 상담 받기
            </Button>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 px-8 py-3">
                연락처 보기
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <Link to="/" className="flex items-center space-x-2 mb-4">
                <Shield className="h-6 w-6 text-blue-400" />
                <span className="text-xl font-bold">Fremont Tech</span>
                <span className="text-sm text-gray-400">프리몬테크</span>
              </Link>
              <p className="text-gray-400 mb-4">
                신뢰할 수 있는 기술 파트너로서 고객의 성공을 위한<br />
                최고의 솔루션을 제공합니다.
              </p>
              <p className="text-sm text-gray-500">
                © 2024 Fremont Tech. All rights reserved.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">연락처</h3>
              <div className="space-y-2 text-gray-400">
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4" />
                  <span>010-2864-1942</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>hyungwoo@naver.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4" />
                  <span>대전 중구 대종로 550번길 5 유원오피스텔 1008호</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Globe className="h-4 w-4" />
                  <span>newvision.co.kr</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;